import { Product } from "data/data";

/**
 * Check if a product has any modifier sets that require selection.
 */
export const hasRequiredModifiers = (product: Product): boolean => {
  if (!product.modifierSets || product.modifierSets.length === 0) return false;
  return product.modifierSets.some((ms) => ms.noOfRequired > 0);
};

/**
 * Check if a product has any modifier sets at all.
 */
export const hasModifiers = (product: Product): boolean => {
  return !!product.modifierSets && product.modifierSets.length > 0;
};

/**
 * Calculate the total additional price from selected modifiers.
 */
export const getModifierPrice = (
  product: Product,
  modifierState: Record<number, Record<number, boolean>>
): number => {
  let total = 0;
  if (!product.modifierSets) return total;

  product.modifierSets.forEach((modifierSet) => {
    const setSelections = modifierState[modifierSet.id];
    if (!setSelections) return;

    modifierSet.modifierSetModifierItems?.forEach((modifierItem) => {
      if (setSelections[modifierItem.id]) {
        total += modifierItem.modifierItem.onlinePrice;
      }
    });
  });

  return total;
};

interface ModifierValidationResult {
  valid: boolean;
  errors: Array<{
    modifierSetId: number;
    modifierSetName: string;
    message: string;
  }>;
}

/**
 * Validate that all required modifier sets have valid selections.
 */
export const validateModifiers = (
  product: Product,
  modifierState: Record<number, Record<number, boolean>>
): ModifierValidationResult => {
  const errors: ModifierValidationResult["errors"] = [];

  if (!product.modifierSets) return { valid: true, errors };

  product.modifierSets.forEach((modifierSet) => {
    const setSelections = modifierState[modifierSet.id] || {};
    const selectedCount = Object.values(setSelections).filter(Boolean).length;

    if (selectedCount < modifierSet.noOfRequired) {
      errors.push({
        modifierSetId: modifierSet.id,
        modifierSetName: modifierSet.name,
        message: `Please select at least ${modifierSet.noOfRequired} modifier${
          modifierSet.noOfRequired > 1 ? "s" : ""
        }.`,
      });
    }

    if (modifierSet.maximum && selectedCount > modifierSet.maximum) {
      errors.push({
        modifierSetId: modifierSet.id,
        modifierSetName: modifierSet.name,
        message: `Please select at most ${modifierSet.maximum} modifiers.`,
      });
    }
  });

  return { valid: errors.length === 0, errors };
};

/**
 * Get the list of selected modifier item names for display (e.g., in cart).
 */
export const getSelectedModifierNames = (
  product: Product,
  modifierState: Record<number, Record<number, boolean>>
): string[] => {
  const names: string[] = [];
  if (!product.modifierSets) return names;

  product.modifierSets.forEach((modifierSet) => {
    const setSelections = modifierState[modifierSet.id];
    if (!setSelections) return;

    modifierSet.modifierSetModifierItems?.forEach((modifierItem) => {
      if (setSelections[modifierItem.id]) {
        names.push(modifierItem.modifierItem.name);
      }
    });
  });

  return names;
};
